import { useState, useEffect, useCallback, useMemo } from "react";
import { fmtDate } from "@/lib/format";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusChip } from "@/components/StatusChip";
import { EmptyState } from "@/components/Primitives";
import { toast } from "sonner";
import { Search, MapPin, CheckCircle2 } from "lucide-react";

const TYPE_OPTS = [
  { key: "leads", label: "Leads" }, { key: "businesses", label: "Businesses / Advertisers" },
  { key: "opportunities", label: "Opportunities" }, { key: "contracts", label: "Contracts" },
  { key: "tasks", label: "Tasks" }, { key: "events", label: "Events" }, { key: "renewals", label: "Renewals" },
];
const ALL = "__all";

export default function UnassignedTerritory() {
  const [data, setData] = useState({ items: [], total: 0, by_type: {}, assignable_users: [] });
  const [territories, setTerritories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [assignTerr, setAssignTerr] = useState("");
  const [assignUser, setAssignUser] = useState("");
  const [busy, setBusy] = useState(false);
  // filters
  const [type, setType] = useState(ALL);
  const [city, setCity] = useState("");
  const [assignee, setAssignee] = useState(ALL);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (type !== ALL) p.set("types", type);
    if (city) p.set("city", city);
    if (assignee !== ALL) p.set("assignee", assignee);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (q) p.set("q", q);
    api.get(`/unassigned/records?${p.toString()}`).then((r) => { setData(r.data); setSelected([]); })
      .catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  }, [type, city, assignee, dateFrom, dateTo, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/territories").then((r) => setTerritories(r.data.items)); }, []);

  const items = data.items;
  const key = (i) => `${i.type}:${i.id}`;
  const allChecked = items.length > 0 && items.every((i) => selected.includes(key(i)));
  const toggle = (i) => setSelected((s) => s.includes(key(i)) ? s.filter((x) => x !== key(i)) : [...s, key(i)]);

  const assign = async () => {
    if (!selected.length) { toast.error("Select at least one record"); return; }
    if (!assignTerr) { toast.error("Choose a territory to assign"); return; }
    setBusy(true);
    const payload = {
      items: selected.map((k) => { const [t, id] = k.split(":"); return { type: t, id }; }),
      territory: assignTerr, assigned_to: assignUser || null,
    };
    try {
      const { data: res } = await api.post("/unassigned/assign", payload);
      toast.success(`Assigned territory to ${res.updated} record(s)`);
      setSelected([]); setAssignTerr(""); setAssignUser(""); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clearFilters = () => { setType(ALL); setCity(""); setAssignee(ALL); setDateFrom(""); setDateTo(""); setQ(""); };
  const hasFilters = type !== ALL || city || assignee !== ALL || dateFrom || dateTo || q;

  return (
    <div data-testid="admin-unassigned">
      <div className="mb-4 rounded-lg border border-[#0094A8]/30 bg-[#0094A8]/5 p-3 text-xs text-[#0b6b78] flex items-center gap-2">
        <MapPin className="h-4 w-4 shrink-0" />
        Records with no territory are hidden from Sales Representatives. Assign the correct territory here to make them visible to the right reps.
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 mb-3">
        <div className="relative lg:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search records…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="unassigned-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="unassigned-f-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All types</SelectItem>{TYPE_OPTS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="City…" value={city} onChange={(e) => setCity(e.target.value)} data-testid="unassigned-f-city" />
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger data-testid="unassigned-f-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any assignee</SelectItem>
            <SelectItem value="__unassigned">Unassigned</SelectItem>
            {data.assignable_users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Created from" data-testid="unassigned-f-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Created to" data-testid="unassigned-f-to" />
        </div>
      </div>
      {hasFilters && <button onClick={clearFilters} className="mb-3 text-xs text-[#0094A8] hover:underline" data-testid="unassigned-clear-filters">Clear filters</button>}

      {/* Bulk assign bar */}
      <div className="mb-4 flex flex-col lg:flex-row lg:items-end gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex-1">
          <Label className="text-xs font-semibold text-slate-600">Assign territory to {selected.length || "0"} selected</Label>
          <Select value={assignTerr} onValueChange={setAssignTerr}>
            <SelectTrigger className="mt-1" data-testid="unassigned-assign-territory"><SelectValue placeholder="Select territory…" /></SelectTrigger>
            <SelectContent>{territories.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs font-semibold text-slate-600">Change assignee (optional)</Label>
          <Select value={assignUser} onValueChange={setAssignUser}>
            <SelectTrigger className="mt-1" data-testid="unassigned-assign-user"><SelectValue placeholder="Keep current owner" /></SelectTrigger>
            <SelectContent>{data.assignable_users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={assign} disabled={busy || !selected.length || !assignTerr} data-testid="unassigned-assign-btn">
          <CheckCircle2 className="h-4 w-4" />Assign {selected.length || ""}
        </Button>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div className="cc-card"><EmptyState icon={MapPin} title="Nothing unassigned" hint="Every territory-based record has a territory. Reps can see the records in their areas." /></div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[920px]">
            <thead><tr className="border-b bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-3 py-3 w-10"><Checkbox checked={allChecked} onCheckedChange={(v) => setSelected(v ? items.map(key) : [])} data-testid="unassigned-select-all" /></th>
              <th className="px-3 py-3">Record</th><th className="px-3 py-3">Type</th><th className="px-3 py-3">Owner</th>
              <th className="px-3 py-3">City</th><th className="px-3 py-3">Created</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Territory</th>
            </tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={key(i)} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`unassigned-row-${i.id}`}>
                  <td className="px-3 py-2.5"><Checkbox checked={selected.includes(key(i))} onCheckedChange={() => toggle(i)} data-testid={`unassigned-check-${i.id}`} /></td>
                  <td className="px-3 py-2.5 font-medium text-slate-800">{i.name}</td>
                  <td className="px-3 py-2.5 text-slate-500">{i.type_label}</td>
                  <td className="px-3 py-2.5 text-slate-500">{i.assignee_name}</td>
                  <td className="px-3 py-2.5 text-slate-500">{i.city || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-500">{i.created_at ? fmtDate(i.created_at) : "—"}</td>
                  <td className="px-3 py-2.5"><StatusChip value={i.status} tone="slate" /></td>
                  <td className="px-3 py-2.5"><span className="inline-flex rounded-full bg-[#FE3967]/10 px-2 py-0.5 text-xs font-medium text-[#C42607]">None</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
